import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import { Button } from './ui/button';

/**
 * Smart install prompt that shows after 60s or 3 page views, dismissible, 
 * and remembers dismissal for 7 days.
 */
const InstallPrompt = () => {
  const { canInstall, promptInstall } = useInstallPrompt();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!canInstall) return;

    // Check if user dismissed recently
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Show after 60s delay
    const timer = setTimeout(() => setVisible(true), 60000);
    return () => clearTimeout(timer);
  }, [canInstall]);

  const handleInstall = async () => {
    await promptInstall();
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa_install_dismissed', String(Date.now()));
    setVisible(false);
  };

  if (!visible || !canInstall) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-[9998] bg-[#0F2C3E] text-white rounded-xl shadow-2xl p-4 animate-in slide-in-from-bottom duration-500"
      role="dialog"
      aria-label="Install app"
      data-testid="install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
        aria-label="Dismiss"
        data-testid="install-prompt-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="bg-[#D4A017] rounded-lg p-2.5 shrink-0">
          <Download className="h-5 w-5 text-[#0F2C3E]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm mb-1">Add Discover Essex to your Home Screen</h3>
          <p className="text-xs text-white/70 mb-3 leading-relaxed">
            Get instant access to local businesses, jobs & events. Works offline too.
          </p>
          <Button
            size="sm"
            onClick={handleInstall}
            className="bg-[#D4A017] hover:bg-[#c4920f] text-[#0F2C3E] font-semibold text-xs"
            data-testid="install-prompt-btn"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Install App
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
